import type {
    AnimationPair,
    CreateTweenAnimationParams,
} from './strategies/types';
import {targetStrategy} from './strategies/targetStrategy';
import {deltaStrategy} from './strategies/deltaStrategy';
import {pulseStrategy} from './strategies/pulseStrategy';

// Select and run one of the tween strategies against a mesh property axis.
//
// Modes (selected via the `mode` discriminant on the params object):
//   - 'target' (default): "arrive exactly here" — writes absolute values each
//     frame. The tween owns the axis; concurrent writers are overwritten.
//   - 'delta':            "move by this amount" — incremental‑additive writes
//     producing a permanent Δ shift, without ever reading the mesh's absolute
//     value. Concurrent writers on the same axis are preserved.
//   - 'pulse':            "excursion and retract" — incremental‑additive
//     writes that fully cancel on completion, preserving any other writer's
//     contribution to the same axis.
//
// Adding a new mode is a three‑step change, each step in exactly one place:
//   1. Add a new `<Mode>Params` interface in `strategies/types.ts` and extend
//      the `CreateTweenAnimationParams` union.
//   2. Add a new file `strategies/<mode>Strategy.ts` exporting a pure
//      `(params: <Mode>Params) => AnimationPair` function.
//   3. Add one `case` to the switch below.
//
// If step 3 is forgotten, the `never` assignment in the default branch fails
// the build — that's the OCP safety net that keeps this dispatcher honest.
//
// Concrete parameter types (`TargetParams`, `DeltaParams`, `PulseParams`,
// `CommonParams`, etc.) live in `./strategies/types` and can be imported
// directly from there by callers that need to build a params object
// polymorphically.
export const createTweenAnimation = (
    params: CreateTweenAnimationParams
): AnimationPair => {
    switch (params.mode) {
        case 'delta':
            return deltaStrategy(params);
        case 'pulse':
            return pulseStrategy(params);
        case undefined:
        case 'target':
            return targetStrategy(params);
        default: {
            // Unreachable for callers who respect the discriminated union; the
            // `never` assignment is the compile‑time exhaustiveness check.
            const _exhaustive: never = params;
            const unhandled = _exhaustive as unknown as {mode?: string};
            throw new Error(
                `createTweenAnimation: unknown mode '${unhandled.mode}'`
            );
        }
    }
};
